import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, PoundSterling, Calendar, Star, BarChart3, Crown, Lock } from 'lucide-react';

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/providers/me/analytics');
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" /></div>;
  if (!data) return <p className="text-center text-stone-500 py-16">No analytics data available.</p>;

  const isPro = data.tier === 'pro';
  const monthlyData = data.monthly_data || [];
  const topServices = data.top_services || [];
  const maxRevenue = Math.max(...monthlyData.map(m => m.revenue), 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Calendar} label="Total Bookings" value={data.total_bookings} />
        <StatCard icon={PoundSterling} label="Total Revenue" value={`£${data.total_revenue.toFixed(2)}`} />
        <StatCard icon={TrendingUp} label="Avg Booking Value" value={`£${data.average_booking_value.toFixed(2)}`} />
        <StatCard icon={Users} label="Unique Clients" value={data.repeat_client_count} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Calendar} label="Confirmed" value={data.confirmed_bookings} sub="bookings" color="text-green-600" />
        <StatCard icon={Calendar} label="Cancelled" value={data.cancelled_bookings} sub="bookings" color="text-red-500" />
        <StatCard icon={Star} label="Rating" value={data.rating ? `${data.rating} / 5` : 'No reviews'} sub={data.review_count ? `${data.review_count} reviews` : ''} color="text-[#D4AF37]" />
      </div>

      {/* Monthly Revenue Chart */}
      <div className="card-surface p-4 sm:p-6">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
          Monthly Overview (Last 6 Months)
        </h3>
        <div className="space-y-3">
          {monthlyData.map(month => {
            const pct = maxRevenue > 0 ? (month.revenue / maxRevenue) * 100 : 0;
            const label = new Date(month.month + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
            return (
              <div key={month.month} className="flex items-center gap-3" data-testid={`month-${month.month}`}>
                <span className="text-sm text-stone-500 w-20 text-right">{label}</span>
                <div className="flex-1 h-8 bg-[#F9F6F0] rounded-lg overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#C9A030] rounded-lg transition-all duration-500"
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 justify-between">
                    <span className="text-xs font-medium text-[#1A1A1A]">{month.bookings} booking{month.bookings !== 1 ? 's' : ''}</span>
                    <span className="text-xs font-semibold text-[#1A1A1A]">£{month.revenue.toFixed(0)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Services (Pro only) */}
      {isPro && topServices.length > 0 && (
        <div className="card-surface p-4 sm:p-6">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">Top Services</h3>
          <div className="space-y-2">
            {topServices.map((svc, i) => (
              <div key={svc.service_id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-[#F9F6F0]" data-testid={`top-service-${i}`}>
                <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-[#1A1A1A]">{svc.name}</span>
                <span className="text-sm text-stone-500">{svc.bookings} bookings</span>
                <span className="text-sm font-medium text-[#D4AF37]">£{svc.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Free-tier upsell, shown in place of the Pro-only advanced sections. */}
      {!isPro && (
        <div
          className="relative overflow-hidden rounded-xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#1A1A1A] to-[#2A2520] p-5"
          data-testid="analytics-pro-upsell"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="rounded-lg bg-[#D4AF37]/20 p-2 shrink-0">
                <Crown className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Unlock advanced analytics with Pro</p>
                <p className="text-xs text-stone-400 mt-0.5">
                  Booking trends, popular hours &amp; days, top-performing services, client retention, and previous-period comparison.
                </p>
              </div>
            </div>
            <Link
              to="/provider/dashboard?tab=earnings"
              className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37] px-4 py-2 text-sm font-medium text-[#1A1A1A] hover:bg-[#c9a030] shrink-0"
              data-testid="analytics-upgrade-btn"
            >
              <Lock className="h-3.5 w-3.5" />
              Upgrade to Pro
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#D4AF37]' }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-xs text-stone-500">{label}</span>
      </div>
      <p className={`text-xl font-heading font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-stone-400">{sub}</p>}
    </div>
  );
}
